import React, { useState, useEffect, useRef } from 'react';
import { Sun, Moon, Search, Tag, ExternalLink } from 'lucide-react';

const baseUrl = 'https://api.softhubs.com.br'

const PAGE_SIZE_OPTIONS = [10, 50, 100];

const getInitialTagsFromUrl = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const params = new URLSearchParams(window.location.search);
  const values = params.getAll('tags');

  if (values.length === 0) {
    return [];
  }

  const normalized = [];

  values.forEach((value) => {
    value.split(',').forEach((tag) => {
      const trimmed = tag.trim();
      if (trimmed) {
        normalized.push(trimmed.toUpperCase());
      }
    });
  });

  if (normalized.length === 0) {
    return [];
  }

  return Array.from(new Set(normalized));
};

const ThemeToggle = () => {
  const [isDark, setIsDark] = useState(() => {
    // Verifica se está no navegador e se há preferência salva
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    const root = window.document.documentElement;

    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      aria-label="Toggle theme"
    >
      {isDark ? (
        <Sun className="w-5 h-5 text-gray-800 dark:text-gray-200" />
      ) : (
        <Moon className="w-5 h-5 text-gray-800 dark:text-gray-200" />
      )}
    </button>
  );
};

const Header = ({ onToggleIframe, isIframeActive, itemsPerPage, onPageSizeChange, pageSizeOptions = PAGE_SIZE_OPTIONS }) => {
  const logoLight = 'https://download.softhubs.com.br/files/LOGO/soft_logo_black.png';
  const logoDark = 'https://download.softhubs.com.br/files/LOGO/soft_logo_white.png';

  const sizeOptions = pageSizeOptions ?? PAGE_SIZE_OPTIONS;
  const controlsRef = useRef(null);
  const [controlsWidth, setControlsWidth] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateWidth = () => {
      if (controlsRef.current) {
        setControlsWidth(controlsRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [sizeOptions, itemsPerPage, isIframeActive]);

  return (
    <header className={`bg-white dark:bg-gray-800 shadow-md ${isIframeActive ? 'w-1/2' : 'w-full'} transition-all duration-300`}>
      <div className="p-3 flex items-center justify-between">
        <div
          className="flex-shrink-0"
          style={{ width: controlsWidth || undefined }}
          aria-hidden="true"
        />
        <div className="flex flex-col items-center justify-center text-center">
          <img
            src={logoLight}
            alt="Logo Light"
            className="w-72 h-auto block dark:hidden cursor-pointer"
            onClick={() => window.location.reload()}
          />
          <img
            src={logoDark}
            alt="Logo Dark"
            className="w-72 h-auto hidden dark:block cursor-pointer"
            onClick={() => window.location.reload()}
          />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Localize respostas rapidamente. (FAQ +)
          </p>
        </div>
        <div className="flex items-center gap-3" ref={controlsRef}>
          <div
            className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
            role="group"
            aria-label="Itens por pagina"
          >
            {sizeOptions.map((option) => (
              <button
                key={option}
                onClick={() => onPageSizeChange?.(option)}
                className={`px-2.5 py-1 text-sm rounded-md transition-colors ${itemsPerPage === option
                  ? 'bg-blue-500 text-white dark:bg-blue-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                aria-pressed={itemsPerPage === option}
                aria-label={`Mostrar ${option} itens por pagina`}
              >
                {option}
              </button>
            ))}
          </div>
          <button
            onClick={onToggleIframe}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle iframe"
          >
            {isIframeActive ? (
              <svg className="h-6 w-6 text-gray-600 dark:text-gray-300" viewBox="0 0 24 24" stroke="currentColor" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6 text-gray-600 dark:text-gray-300" viewBox="0 0 24 24" stroke="currentColor" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            )}
          </button>
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};

const TagInput = ({ tags = [], onTagAdd, onTagRemove, suggestions = [], readOnly }) => {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      onTagAdd(input.trim().toUpperCase()); // Converte para maiúsculas      
      setInput('');
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      onTagRemove(tags[tags.length - 1]);
    }
  };

  return (
    <div className="relative w-full">
      {/* Ícone dentro do input */}
      {/* Ícone dentro do input */}
      <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />

      <div className="w-full h-10 pl-10 p-2 border rounded dark:bg-gray-700 dark:text-white focus-within:ring-2 focus-within:ring-blue-500 flex items-center">
        {/* Tags */}
        {tags.map(tag => {
          const tagInfo = suggestions.find(s => s.nome === tag) || {};
          return (
            <span
              key={tag}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-sm dark:text-white"
              style={{
                backgroundColor: tagInfo.cor ? `${tagInfo.cor}20` : '#93C5FD',
                color: tagInfo.cor || '#1D4ED8'
              }}
            >
              {tag}
              {!readOnly && (
                <button
                  onClick={() => onTagRemove(tag)}
                  className="hover:opacity-70 dark:text-white"
                  style={{ color: tagInfo.cor || '#1D4ED8' }}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        {/* Input de tag */}
        {!readOnly && (
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value.toUpperCase())} // Converte para maiúsculas
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="flex-1 outline-none bg-transparent dark:text-white placeholder-gray-400"
            placeholder={tags.length === 0 ? "Adicione tags..." : ""}
          />
        )}
      </div>

      {/* Lista de sugestões */}
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions
            .filter(s => !tags.includes(s.nome) && s.nome.toLowerCase().includes(input.toLowerCase()))
            .map(tag => (
              <li
                key={tag.nome}
                onClick={() => {
                  onTagAdd(tag.nome.toUpperCase()); // Converte para maiúsculas                 
                  setInput('');
                  setShowSuggestions(false);
                }}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white flex items-center gap-2"
              >
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: tag.cor }}
                ></span>
                {tag.nome}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

const FAQCard = ({ faq, availableTags, onSave, onMouseEnter, isActive, onSelect }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nome: faq.nome,
    descricao: faq.descricao || '',
    tags: faq.tags ? faq.tags : [],
  });

  const timerRef = useRef(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const incrementCount = async (id) => {
    try {
      const response = await fetch(`${baseUrl}/execute-procedure`, {
        method: 'POST',
        headers: {
          'X-API-Version': '2',
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Basic YWRtaW46c3VwZXJzZWNyZXQ=',
        },
        body: JSON.stringify({
          database: '[dbProjetos]',
          procedure: 'sp_Incrementar_Count',
          params: { id },
        }),
      });

      if (!response.ok) throw new Error(`Erro ao incrementar contador: ${response.statusText}`);
      //console.log('Contador incrementado com sucesso');
    } catch (error) {
      console.error('Erro ao incrementar contador:', error);
    }
  };

  const handleOpenExternal = async () => {
    window.open(faq.link_detalhe, '_blank', 'noopener,noreferrer');
    await incrementCount(faq.id); // Incrementa o contador antes de abrir o link
  };

  const handleCardClick = () => {
    if (!isEditing && onSelect) {
      onSelect();
    }
  };

  const handleMouseDown = (e) => {
    if (e.shiftKey) { // Só ativa a edição se Shift estiver pressionado
      timerRef.current = setTimeout(() => {
        setIsEditing(true);
      }, 1000);
    }
  };

  const handleMouseUp = () => {
    clearTimeout(timerRef.current);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleTagAdd = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData((prevData) => ({
        ...prevData,
        tags: [...prevData.tags, tag],
      }));
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData((prevData) => ({
      ...prevData,
      tags: prevData.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        database: '[dbProjetos]',
        entity: 'tb_pc_faq',
        data: {
          descricao: formData.descricao,
          tags: JSON.stringify(formData.tags),
        },
        filters: {
          id: faq.id,
        },
      };

      const response = await fetch(`${baseUrl}/update-data`, {
        method: 'PUT',
        headers: {
          'X-API-Version': '2',
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Basic YWRtaW46c3VwZXJzZWNyZXQ=',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Erro ao salvar: ${response.statusText}`);
      //console.log('FAQ atualizado com sucesso');

      // Chama o endpoint para executar a procedure após a atualização da FAQ
      const procedurePayload = {
        database: '[dbProjetos]',
        procedure: 'sp_Atualizar_FAQ_Tags',
      };

      const procedureResponse = await fetch(`${baseUrl}/execute-procedure`, {
        method: 'POST',
        headers: {
          'X-API-Version': '2',
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Basic YWRtaW46c3VwZXJzZWNyZXQ=',
        },
        body: JSON.stringify(procedurePayload),
      });

      if (!procedureResponse.ok) {
        throw new Error(`Erro ao executar procedure: ${procedureResponse.statusText}`);
      }

      console.log('Procedure executada com sucesso');

      // Certifique-se de que `onSave` está definido antes de chamá-lo
      if (onSave) {
        onSave({
          ...faq,
          nome: formData.nome,
          descricao: formData.descricao,
          tags: formData.tags,
        });
      }

      // Fecha o modo de edição
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao salvar FAQ:', error);
    }
  };

  const handleCopyLink = () => {
    let inputTemp = document.createElement("input");
    inputTemp.value = faq.link_detalhe;
    document.body.appendChild(inputTemp);
    inputTemp.select();
    document.execCommand("copy");
    document.body.removeChild(inputTemp);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const getTagColor = (tagName) => {
    const tagInfo = availableTags.find(tag => tag.nome === tagName);
    return tagInfo ? tagInfo.cor : '#93C5FD';
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={isEditing ? undefined : handleCardClick}
      onMouseEnter={() => onMouseEnter(`https://faq.softcom.services/helptools/id/${faq.id}`, faq.id)} // Passa o ID do FAQ ao entrar no card
      onMouseLeave={() => setIsPreviewVisible(false)}
      className={`border transition-all duration-150 ease-in-out cursor-pointer relative rounded-lg p-3 shadow-sm hover:shadow-md ${
        isActive
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' // Estilo para card ativo
          : 'border-gray-300 dark:border-gray-800 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700' // Estilo padrão
      }`}
    >
      {/* Botão de Copiar Link */}
      {!isEditing && (
        <div className="absolute top-2 right-2 flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopyLink();
            }}
            className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-all ${
              isCopied
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {isCopied ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </>
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenExternal();
            }}
            className="text-xs px-2 py-1 rounded flex items-center gap-1 transition-all bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-700"
            aria-label="Abrir em nova aba"
            title="Abrir em nova aba"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
      {isEditing ? (
        // Modo de edição
        <div>
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleInputChange}
            className="block w-full mb-2 p-1 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            placeholder="Nome"
          />
          <textarea
            name="descricao"
            value={formData.descricao}
            onChange={handleInputChange}
            className="block w-full mb-2 p-1 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            placeholder="Descrição"
          />
          <TagInput
            tags={formData.tags}
            onTagAdd={handleTagAdd}
            onTagRemove={handleTagRemove}
            suggestions={availableTags}
            readOnly={false}
          />
          <button
            onClick={handleSave}
            className="px-3 py-1 bg-blue-500 text-white rounded border border-blue-500 dark:border-blue-700"
          >
            Salvar
          </button>
        </div>
      ) : (
        // Modo de visualização
        <>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
            {faq.nome}
          </h3>
          {faq.descricao && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
              {faq.descricao}
            </p>
          )}
          {faq.tags && faq.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {faq.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 rounded text-xs"
                  style={{
                    backgroundColor: `${getTagColor(tag)}20`,
                    color: getTagColor(tag),
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          {/* Detalhes na parte inferior */}
          <div className="mt-1 pb-2">
            <div className="absolute bottom-1 right-2 text-xs text-gray-300 dark:text-gray-600 font-mono">
              <div className="flex items-center space-x-2">
                {/* Autor */}
                <div className="max-w-[180px] overflow-hidden whitespace-nowrap" title={`Autor: ${faq.autor} (${formatDate(faq.data_criado)})`}>
                  <span>Autor: {faq.autor} </span>
                  <span>({formatDate(faq.data_criado)})</span>
                </div>
                <span>|</span>
                {/* Editor */}
                <div className="max-w-[180px] overflow-hidden whitespace-nowrap" title={`Editor: ${faq.editor} (${formatDate(faq.data_editado)})`}>
                  <span>Editor: {faq.editor} </span>
                  <span>({formatDate(faq.data_editado)})</span>
                </div>
                <span>|</span>
                {/* FAQ ID */}
                <div className="max-w-[80px] overflow-hidden whitespace-nowrap" title={`FAQ: ${faq.id}`}>
                  <span>FAQ: {faq.id}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {isPreviewVisible && faq.link_detalhe && (
        <div className="absolute left-full ml-4 top-0 w-[600px] h-[400px] bg-white dark:bg-gray-800 shadow-lg border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
          <iframe src={faq.link_detalhe} className="w-full h-full" sandbox="allow-scripts allow-same-origin" />
        </div>
      )}
    </div>
  );
};

const DebouncedSearchInput = ({ onSearch, delay = 500, placeholder = "Pesquisar por nome, descrição ou ID..." }) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, delay);

    return () => clearTimeout(handler);
  }, [inputValue, delay]);

  useEffect(() => {
    onSearch(debouncedValue); // Agora chama mesmo quando debouncedValue é ""
  }, [debouncedValue, onSearch]);

  return (
    <div className="relative flex items-center">
      <Search className="absolute left-3 text-gray-400 dark:text-gray-500" size={20} />
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        className="w-full pl-10 p-2 border rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
};

const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [searchTags, setSearchTags] = useState(() => getInitialTagsFromUrl());
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [isIframeActive, setIsIframeActive] = useState(false); // Estado para controlar o iframe
  const [iframeUrl, setIframeUrl] = useState(''); // Estado para controlar a URL do iframe
  const [isIframeFixed, setIsIframeFixed] = useState(false);
  const isFirstTagSyncRef = useRef(true);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > 100) { // Define um limite para fixar
        setIsIframeFixed(true);
      } else {
        setIsIframeFixed(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    fetchFAQs(currentPage);
  }, [currentPage, searchTags, searchTerm, itemsPerPage]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isFirstTagSyncRef.current) {
      isFirstTagSyncRef.current = false;
      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.delete('tag');
    params.delete('tags');

    if (searchTags.length) {
      params.set('tags', searchTags.join(','));
    }

    const newQuery = params.toString();
    const queryPart = newQuery ? '?' + newQuery : '';
    const newUrl = window.location.pathname + queryPart + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }, [searchTags]);

  const fetchTags = async () => {
    try {
      const response = await fetch(`${baseUrl}/query-data?page=1&per_page=100`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-API-Version': '2',
          'Authorization': 'Basic YWRtaW46c3VwZXJzZWNyZXQ='
        },
        body: JSON.stringify({
          database: '[dbProjetos]',
          entity: 'tb_pc_tags'
        })
      });
      const result = await response.json();
      setAvailableTags(result.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchFAQs = async (page) => {
    setLoading(true);
    try {
      let whereClause = '';
      if (searchTags.length > 0) {
        whereClause = searchTags.map(tag => `tags LIKE '%${tag}%'`).join(' AND ');
      }
      if (searchTerm.trim() !== '') {
        const term = searchTerm.trim();
        const searchConditions = [
          `nome LIKE '%${term}%'`,
          `descricao LIKE '%${term}%'`,
          `id = ${parseInt(term, 10) || 0}`
        ].join(' OR ');
        whereClause = whereClause ? `${whereClause} AND (${searchConditions})` : `(${searchConditions})`;
      }

      const response = await fetch(`${baseUrl}/query-data?page=${page}&per_page=${itemsPerPage}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-API-Version': '3',
          'Authorization': 'Basic YWRtaW46c3VwZXJzZWNyZXQ='
        },
        body: JSON.stringify({
          database: '[dbProjetos]',
          entity: 'vw_faqs',
          where: whereClause,
          order_by: '[tags] DESC, [count] DESC, [id] DESC'
        })
      });

      const result = await response.json();
      const processedFaqs = result.data ? result.data.map(faq => ({
        ...faq,
        tags: faq.tags ? JSON.parse(faq.tags) : []
      })) : [];

      setFaqs(processedFaqs);
      setPagination(result.meta || { current_page: 1, last_page: 1 });

      if (processedFaqs.length === 0) {
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    setCurrentPage(1); // Resetar para a primeira página ao aplicar uma nova pesquisa
  };

  const handleToggleIframe = () => {
    setIsIframeActive(!isIframeActive);
  };

  const [iframeLoading, setIframeLoading] = useState(false);
  const [activeFAQId, setActiveFAQId] = useState(null);

  const handleLoadFaq = (url, faqId) => {
    if (activeFAQId === faqId && iframeUrl === url) return;
    setIframeUrl('');
    setIframeLoading(true);
    setActiveFAQId(faqId);
    setIframeUrl(url);
  };

  const handleMouseEnterCard = (url, faqId) => {
    //console.log(url);
    if (isIframeActive) {
      // Verifica se o FAQ já está ativo
      if (activeFAQId === faqId) {
        return; // Sai da função se o FAQ já está ativo
      }
  
      // Define o estado de carregamento e atualiza a URL do iframe
      setIframeUrl(''); // Limpa a URL temporariamente
      setIframeLoading(true); // Ativa o estado de carregamento
      setActiveFAQId(faqId); // Define o ID do FAQ ativo
      setIframeUrl(url); // Define a nova URL
    }
  };

  const handleIframeLoad = () => {
    setIframeLoading(false); // Define o estado de carregamento como falso quando o iframe termina de carregar
  };

  const handleUpdateFAQ = async (updatedFAQ) => {
    setFaqs((prevFaqs) =>
      prevFaqs.map((faq) => (faq.id === updatedFAQ.id ? updatedFAQ : faq))
    );
    // Busca novamente as tags atualizadas
    await fetchTags();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className={isIframeActive ? 'max-w-1/2' : 'max-w-full'}>
        <Header
          onToggleIframe={handleToggleIframe}
          isIframeActive={isIframeActive}
          itemsPerPage={itemsPerPage}
          onPageSizeChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <main className="relative flex">
        {/* Coluna principal com os cards */}
        <div
          className={`transition-all duration-300 ease-in-out p-3 ${isIframeActive
            ? 'w-1/2'
            : 'w-full max-w-4xl mx-auto'
            }`}
        >
          <div className="mb-8 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md space-y-4">
            <DebouncedSearchInput
              onSearch={(value) => setSearchTerm(value)}
              placeholder="Pesquisar por nome, descrição ou ID..."
            />
            <div className="relative flex items-center">
              <Tag className="absolute left-3 text-gray-400 dark:text-gray-500" size={20} />
              <TagInput
                tags={searchTags}
                onTagAdd={tag => {
                  if (!searchTags.includes(tag)) {
                    setSearchTags([...searchTags, tag]);
                    setCurrentPage(1);
                  }
                }}
                onTagRemove={tag => {
                  setSearchTags(searchTags.filter(t => t !== tag));
                  setCurrentPage(1);
                }}
                suggestions={availableTags}
                className="w-full pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <span className="animate-pulse text-lg font-semibold text-gray-700 dark:text-gray-300">
                Carregando...
              </span>
            </div>
          ) : (
            <div className="space-y-4">
              {faqs.map(faq => (
                <FAQCard
                  key={faq.id}
                  faq={faq}
                  availableTags={availableTags}
                  onSave={handleUpdateFAQ}
                  onMouseEnter={() => handleMouseEnterCard(`https://faq.softcom.services/helptools/id/${faq.id}`, faq.id)}
                  isActive={activeFAQId === faq.id} // Passa true se este FAQ estiver ativo
                  onSelect={() => {
                    const url = `https://faq.softcom.services/helptools/id/${faq.id}`;
                    if (!isIframeActive) setIsIframeActive(true);
                    handleLoadFaq(url, faq.id);
                  }}
                />
              ))}
            </div>
          )}

          {/* Paginação */}
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={pagination?.last_page === 1 || faqs.length === 0}
              className={`px-4 py-2 rounded-lg transition-colors ${pagination?.last_page === 1 || faqs.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                : 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
                }`}
            >
              Anterior
            </button>
            <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
              Página {faqs.length === 0 ? 0 : pagination.current_page} de {faqs.length === 0 ? 0 : pagination.last_page}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, pagination.last_page))}
              disabled={pagination?.last_page === 1 || faqs.length === 0}
              className={`px-4 py-2 rounded-lg transition-colors ${pagination?.last_page === 1 || faqs.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                : 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
                }`}
            >
              Próxima
            </button>
          </div>
        </div>

        {/* Coluna do iframe com transição */}
        <div
          className={`fixed top-0 right-0 h-screen bg-white dark:bg-gray-800 shadow-md overflow-hidden transition-all duration-300 ease-in-out ${isIframeActive
            ? 'w-1/2 opacity-100 translate-x-0'
            : 'w-0 opacity-0 translate-x-full'
            }`}
        >
          {iframeLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
            </div>
          )}
          <iframe
            src={iframeUrl}
            className={`w-full h-full rounded-lg border border-gray-300 dark:border-gray-800 ${iframeLoading ? 'hidden' : '' // Oculta o iframe enquanto estiver carregando
              }`}
            style={{ backgroundColor: 'white' }}
            sandbox="allow-scripts allow-same-origin"
            onLoad={handleIframeLoad} // Manipulador para detectar quando o iframe termina de carregar
          />
        </div>
      </main>
    </div>
  );
};

export default FAQManager;
